import {
  Component,
  Input,
  ViewChild,
  Output,
  EventEmitter,
  OnInit, OnDestroy,
  AfterViewInit,
  ChangeDetectorRef,
  ElementRef,
  ViewEncapsulation
} from '@angular/core';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { map, startWith } from 'rxjs/operators';

import * as uuid from 'uuid';
import { NgForm, NgModel } from '@angular/forms';
import { Observable } from 'rxjs';
import { UtilityService } from '../../services/utility/utility.service';
import { MatChipInputEvent, MatChipList } from '@angular/material/chips';
import { MatAutocomplete, MatAutocompleteSelectedEvent, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { TranslateService } from '../../services/translate/translate.service';

@Component({
  selector: 'sd-chip-control',
  templateUrl: './chip-control.component.html',
  styleUrls: [
    '../../assets/css/form.scss',
    './chip-control.component.scss',
  ],
  encapsulation: ViewEncapsulation.None
})

export class ChipControlComponent implements OnInit, OnDestroy, AfterViewInit {
  private initialized = false;
  private pModel: (string | number)[] = [];
  private pItems: any = [];
  name = uuid.v4();
  separatorKeysCodes: number[] = [ENTER, COMMA];
  selectable = true;
  removable = true;
  @Input() form: NgForm;
  @Input() label: string;
  @Input() valueField: string;
  @Input() displayField: string;
  @Input() displayChipField: string;

  // tslint:disable-next-line: variable-name
  private _items: any[] = [];
  get items(): any[] {
    return this._items;
  }
  @Input() set items(items: any[]) {
    if (!Array.isArray(items)) {
      items = [];
    }
    this.pItems = items;
    if (this.initialized) {
      this.onChanges();
    }
  }
  @Input() limit = 50;
  selectedItems: any[] = [];
  filteredItems: Observable<any[]>;

  ipt: any;

  // model
  // tslint:disable-next-line: variable-name
  private _model: (string | number)[] = [];
  @Input() set model(values: (string | number)[]) {
    if (!Array.isArray(values)) {
      values = [];
    }
    this.pModel = values;
    if (this.initialized) {
      this.onChanges();
    }
  }
  @Output() modelChange = new EventEmitter();

  // Optional
  @Input() disabled: boolean;

  // tslint:disable-next-line: variable-name
  _minItem = 0;
  @Input() set minItem(minItem: number) {
    minItem = +minItem || 0;
    this._minItem = minItem;
    this.formModel?.control.updateValueAndValidity({ onlySelf: false, emitEvent: true });
    this.ref.detectChanges();
  }

  // tslint:disable-next-line: no-output-native
  @Output() change = new EventEmitter();
  @Output() sdChange = new EventEmitter();
  @Output() chipSelected = new EventEmitter<any>();


  @ViewChild('formModel', { static: true }) formModel: NgModel;
  @ViewChild('input') input: ElementRef<HTMLInputElement>;
  @ViewChild('auto') matAutocomplete: MatAutocomplete;
  @ViewChild('chipList', { static: true }) chipList: MatChipList;
  @ViewChild(MatAutocompleteTrigger, { read: MatAutocompleteTrigger }) autocompleteTrigger: MatAutocompleteTrigger;
  constructor(
    private ref: ChangeDetectorRef,
    private utilityService: UtilityService,
    private translateService: TranslateService) {
  }

  ngOnInit() {
    this.filteredItems = this.formModel.valueChanges.pipe(
      startWith<string | { [key: string]: string }>(''),
      map(item => typeof item === 'string' ? item : (item?.[this.displayField] ?? '')),
      map(name => this.filter(name))
    );
  }

  ngAfterViewInit() {
    this.form?.addControl(this.formModel);
    this.formModel?.control.updateValueAndValidity({ onlySelf: false, emitEvent: true });
    this.onChanges();
    this.initialized = true;
    this.ref.detectChanges();
  }

  ngOnDestroy() {
    this.form?.removeControl(this.formModel);
  }

  private onChanges = () => {
    this._model = this.pModel;
    this._items = this.pItems;
    this.selectedItems = this._items.filter(item => this._model.findIndex(value => value === item?.[this.valueField]) !== -1);
    this.formModel?.control.updateValueAndValidity({ onlySelf: false, emitEvent: true });
  }

  filter(value: string): { [key: string]: string }[] {
    return this.items
      .filter(item => {
        if (this.selectedItems.some(e => e[this.valueField] === item[this.valueField])) {
          return false;
        }
        return !value || this.utilityService.changeAliasLowerCase(item[this.displayField])
          .indexOf(this.utilityService.changeAliasLowerCase(value)) !== -1;
      }).filter((item, index) => index < this.limit);
  }

  add(event: MatChipInputEvent): void {
    // Add item only when MatAutocomplete is not open
    // To make sure this does not conflict with OptionSelected Event
    if (!this.matAutocomplete.isOpen) {
      const input = event.input;
      const value = event.value;

      // Add our item
      if ((value || '').trim()) {
        const item = this.items.find(e => e[this.displayField]
          && e[this.displayField].toString().toLowerCase() === value.toString().toLowerCase().trim());
        if (item) {
          this.selectedItems.push(item);
          this.updateModel();
        }
      }

      // Reset the input value
      if (input) {
        input.value = '';
      }
      this.formModel.control.setValue(null);
    }
  }

  remove(item: any): void {
    const index = this.selectedItems.indexOf(item);
    if (index >= 0) {
      this.selectedItems.splice(index, 1);
      this.updateModel();
      this.formModel.reset();
    }
  }

  select(item: any): void {
    this.chipSelected.emit(item);
  }

  selected(event: MatAutocompleteSelectedEvent): void {
    const value = event.option.viewValue;
    // Add our fruit
    if ((value || '').trim()) {
      const item = this.items.find(e => e[this.displayField]
        && e[this.displayField].toString().toLowerCase() === value.toString().toLowerCase().trim());
      this.selectedItems.push(item);
      this.input.nativeElement.value = '';
      this.formModel.control.setValue(null);
      this.updateModel();
      setTimeout(() => {
        this.autocompleteTrigger?.openPanel();
      });
    }
  }

  displayFn = (value: string | { [key: string]: string }): string | undefined => {
    if (typeof value === 'string') {
      if (!this.items) {
        return undefined;
      }
      const selectedItem = this.items.find(item => item[this.valueField] === value);
      if (selectedItem) {
        return selectedItem[this.displayField];
      }
      if (this.items.some(item => item[this.displayField].toLowerCase().indexOf(value.toLowerCase()) !== -1)) {
        return value;
      } else {
        return undefined;
      }
    } else {
      return value ? value[this.displayField] : undefined;
    }
  }

  private updateModel() {
    this._model = this.selectedItems.map(e => e[this.valueField]);
    this.modelChange.emit(this._model);
    this.change.emit(this.selectedItems);
    this.sdChange.emit(this.selectedItems);
    setTimeout(() => {
      this.form?.form?.markAsDirty();
    }, 0);
  }

  validateMinItem = () => {
    if (!this.chipList) {
      return null;
    }
    if (this.selectedItems.length < this._minItem) {
      this.chipList.errorState = true;
      return `${this.translateService.translate('Please select at least')} ${this._minItem}`;
    }
    this.chipList.errorState = false;
    return null;
  }
  clear = ($event?: any) => {
    $event?.stopPropagation();
    this.input.nativeElement.value = '';
    this.ipt = '';
    this.formModel.control.setValue(null);
    this.autocompleteTrigger?.openPanel();
    this.ref.detectChanges();
  }
}
